import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';

import OrganisationInformation from './components/information';
import ImageUpload from './components/image-upload';
import ArchiveOrg from './components/archive-org';
import PayoutConfiguration from './components/payout-configuration';
import OrganisationFeatures from './components/features';
import OrganisationdDefaults from './components/defaults';
import Banding from './components/banding';
import BillingSettings from './components/billing';
import Head from 'src/layout/head/Head';
import { Building2, CreditCard, Globe, RectangleEllipsis, Shield, Receipt } from "lucide-react"
import BinRules from './components/bin-rules';

export const Layout = () => {
  const { organisation, enums } = useSelector((state) => state);
  const location = useLocation();
  const history = useHistory();

  const searchParams = new URLSearchParams(location.search);
  const activeView = searchParams.get('tab') || 'general';

  const setActiveView = (view) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', view);
    history.replace({ search: params.toString() });
  };

  const isAdmin = organisation.is_admin;

  const views = {
    general: { title: 'General', description: 'Basic settings for organisation', adminOnly: false, icon: Building2 },
    billing: { title: 'Billing', adminOnly: true, icon: Receipt, description: 'Configure billing rates' },
    bin: { title: 'Bin Rules', adminOnly: false, icon: RectangleEllipsis, description: 'Configure bin rules' },
    defaults: { title: 'Defaults', adminOnly: false, icon: Globe, description: 'Defaults for organisation' },
    features: { title: 'Features', adminOnly: true, icon: Shield, description: 'Enable or disable features' },
    payments: { title: 'Payments', adminOnly: true, icon: CreditCard, description: 'Configure payment settings' },
  };

  return (
    <React.Fragment>
      <Head title="Settings" />
      <div className="bg-white overflow-hidden flex min-h-[80vh] -mt-10 -mb-10 -mx-8">

        {/* Sidebar */}
        <div className="w-[250px] float-left bg-violet-100 pt-4 space-y-2 border-r border-violet-300">

          {Object.keys(views).map((key) => {
            const view = views[key];
            if (view.adminOnly === true && !isAdmin) return null; // Skip admin-only views for non-admins

            return (
              <button
                key={key}
                onClick={() => setActiveView(key)}
                className={`w-full text-left px-4 py-2 transition-all duration-150 font-mono text-sm ${activeView === key
                    ? "bg-white text-violet-600 font-semibold shadow"
                    : "text-gray-700 hover:bg-white hover:text-violet-500"
                  }`}
              >
                <view.icon className="block float-left mr-2 h-5 w-5 -mt-[1px] font-bold" />
                <div>
                  <span className='uppercase'>{views[key].title}</span>
                  <p className="text-xs pl-8 text-gray-500">{views[key].description}</p>
                </div>
                
              </button>
            )
          })}
        </div>

        {/* Main Content */}
        <div className="float-left p-10 w-full">
          {activeView === 'general' && (
            <React.Fragment>
              <OrganisationInformation organisation={organisation} enums={enums?.enums} isAdmin={isAdmin} />
              <ImageUpload organisation={organisation} isAdmin={isAdmin} />

              {isAdmin && <ArchiveOrg organisation={organisation} isAdmin={isAdmin} />}
            </React.Fragment>
          )}

          {activeView === 'defaults' && (
            <React.Fragment>
              <OrganisationdDefaults organisation={organisation} />
            </React.Fragment>
          )}

          {activeView === 'features' && (
            <React.Fragment>
              <OrganisationFeatures organisation={organisation} isAdmin={isAdmin} />
            </React.Fragment>
          )}

          {activeView === 'payments' && (
            <React.Fragment>
              <Banding organisation={organisation} isAdmin={isAdmin} />
              <PayoutConfiguration organisation={organisation} isAdmin={isAdmin} />
            </React.Fragment>
          )}

          {activeView === 'bin' && (
            <React.Fragment>
              <BinRules />
            </React.Fragment>
          )}

          {activeView === 'billing' && (
            <React.Fragment>
              <BillingSettings organisation={organisation} />
            </React.Fragment>
          )}

        </div>
      </div>
    </React.Fragment>
  );
}

export default Layout;